import ArtAttributionBadge from "@/components/ArtAttributionBadge";
import BetaBadge from "@/components/BetaBadge";
import BottomToolbar from "@/components/BottomToolbar";
import CampaignsModal from "@/components/campaigns/CampaignsModal";
import InviteModal from "@/components/InviteModal";
import MapCanvas from "@/components/MapCanvas";
import MembersModal from "@/components/MembersModal";
import OnboardingModal, { type OnboardingValues } from "@/components/OnboardingModal";
import OrderedBrushStrip from "@/components/OrderedBrushStrip";
import SelectedDecorationStrip from "@/components/SelectedDecorationStrip";
import TilePicker from "@/components/TilePicker";
import Toolbar from "@/components/Toolbar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useToast } from "@/components/ui/ToastProvider";
import { useCampaignRealtimeChannel } from "@/hooks/useCampaignRealtimeChannel";
import { useEffectiveCampaignRole } from "@/hooks/useEffectiveCampaignRole";
import {
  getPublicCampaignJoinInfo,
  listUserCampaigns,
} from "@/lib/campaignsClient";
import { finalizePendingCloudOnboarding } from "@/lib/finalizePendingCloudOnboarding";
import {
  clearLastOpenedShortId,
  getLastOpenedShortId,
  resolveDefaultCampaignShortId,
  setLastOpenedShortId,
} from "@/lib/lastOpenedCampaign";
import {
  hasSeenNotebookHint,
  markNotebookHintSeen,
} from "@/lib/notebookFirstTimeHint";
import { signInWithPassword, signUpWithPassword } from "@/lib/magicLinkAuth";
import { updateCurrentProfile } from "@/lib/profileClient";
import {
  useCampaignByShortIdQuery,
  useUpdateCampaignMutation,
} from "@/lib/queries/campaignQueries";
import { useCurrentProfileQuery } from "@/lib/queries/profileQueries";
import { queryKeys } from "@/lib/queryKeys";
import { setRemovedCampaignNotice } from "@/lib/removedCampaignNotice";
import {
  fetchStarterCampaignDocument,
  fetchStarterCampaignNotebookContent,
} from "@/lib/starterCampaign";
import { getSupabaseBrowserClient, getSupabaseCurrentUser } from "@/lib/supabase";
import {
  clearAnonymousNotebookContent,
  DEFAULT_ANONYMOUS_NOTEBOOK_CONTENT,
  ensureAnonymousNotebookContent,
  saveAnonymousNotebookContent,
} from "@/state/anonymousNotebookStorage";
import {
  clearMapSession,
  clearLegacyMapSession,
  loadLegacyMapSession,
  loadMapSession,
  saveMapSession,
} from "@/state/mapSessionStorage";
import {
  clearPendingCloudOnboarding,
  loadPendingCloudOnboarding,
  savePendingCloudOnboarding,
  type PendingCloudOnboarding,
} from "@/state/pendingCloudOnboarding";
import { setBroadcastMapChange, setBroadcastMapNoteChange, setBroadcastPartyMove } from "@/state/realtimeBroadcast";
import { useCampaignNotebookStore } from "@/state/useCampaignNotebookStore";
import { useCampaignsModalStore } from "@/state/useCampaignsModalStore";
import { useEditorStore } from "@/state/useEditorStore";
import { useKeyboardShortcutsStore } from "@/state/useKeyboardShortcutsStore";
import { useMapNoteDetailStore } from "@/state/useMapNoteDetailStore";
import { useMapStore } from "@/state/useMapStore";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { Hex, MapChangeSnapshot, UserCampaignRecord } from "@west-marches/shared";
import type { RichTextDocument } from "@/components/editor/types";
import {
  collectMapPreloadAssets,
  createMapDocument,
  EMPTY_EDITOR_PERSISTENCE_STATE, getDecorationPaintSetById, getOrderedBrushEntries, getOrderedDecorationBrushEntries,
  getTileSetById, importMapDocument, isOrderedDecorationPaintSet, isOrderedTileSet, preloadMapAssets, preloadMarker, useTileCatalogStore, type ImportedMapDocument
} from "@west-marches/shared";
import {
  DEFAULT_MAP_CAMERA,
  getMapHexKeysSortedByY,
  getVisibleHexKeys,
} from "@/lib/visibleHexes";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isEditableTarget,
  resolveEditorShortcut,
  type TilePickerShortcutRequest,
  type TilePickerShortcutRequestInput,
} from "./editorShortcuts";

// ~16:9 ocean rectangle that yields a hex count comparable to the previous
// radius=30 hexagon (~2,800 hexes). Used as the blank-canvas fallback whenever
// no DB-flagged starter campaign is available.
const DEFAULT_NEW_MAP_BOUNDS = { halfCols: 35, halfRows: 20 } as const;

export type HomeClientEntry =
  | {
      kind: "cloud-campaign";
      shortId: string;
    }
  | {
      kind: "local-root";
      finalizeOnboarding?: boolean;
    };

interface HomeClientProps {
  entry: HomeClientEntry;
}

function reportHomeClientError(action: string, error: unknown, extras?: Record<string, unknown>) {
  Sentry.captureException(error instanceof Error ? error : new Error(action), {
    tags: {
      area: "home-client",
      action,
    },
    extra: extras,
  });
}

export default function HomeClient({ entry }: HomeClientProps) {
  const navigate = useNavigate();
  const { mapNoteId: routedMapNoteId } = useParams({ strict: false });
  const toast = useToast();
  const supabase = useMemo(() => getSupabaseBrowserClient(), []);
  const mapState = useMapStore((s) => s.mapState);
  const initMap = useMapStore((s) => s.initMap);
  const applySnapshot = useMapStore((s) => s.applySnapshot);
  const importMap = useMapStore((s) => s.importMap);

  const history = useEditorStore((s) => s.history);
  const historyIndex = useEditorStore((s) => s.historyIndex);
  const editorUndo = useEditorStore((s) => s.undo);
  const editorRedo = useEditorStore((s) => s.redo);
  const canUndo = useEditorStore((s) => s.canUndo);
  const canRedo = useEditorStore((s) => s.canRedo);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);
  const setEditorMode = useEditorStore((s) => s.setEditorMode);
  const selectedDecorationPaintSetId = useEditorStore(
    (s) => s.selectedDecorationPaintSetId,
  );
  const setDecorationMode = useEditorStore((s) => s.setDecorationMode);
  const restoreEditorPersistence = useEditorStore((s) => s.restorePersistence);
  const setCanEdit = useEditorStore((s) => s.setCanEdit);
  const canEdit = useEditorStore((s) => s.canEdit);
  const setActiveCampaignId = useEditorStore((s) => s.setActiveCampaignId);
  const selectedTileSetId = useEditorStore((s) => s.selectedTileSetId);
  const activeTool = useEditorStore((s) => s.activeTool);
  const editorMode = useEditorStore((s) => s.editorMode);
  const cycleSelectedBrushEntry = useEditorStore((s) => s.cycleSelectedBrushEntry);
  const cycleSelectedDecorationBrushEntry = useEditorStore(
    (s) => s.cycleSelectedDecorationBrushEntry,
  );
  const setSelectedBrushEntry = useEditorStore((s) => s.setSelectedBrushEntry);
  const ensureTileCatalogLoaded = useTileCatalogStore((s) => s.ensureLoaded);

  const [assetsReady, setAssetsReady] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState<
    { loaded: number; total: number } | null
  >(null);
  const [tilePickerOpen, setTilePickerOpen] = useState(false);
  const [tilePickerMode, setTilePickerMode] = useState<"tiles" | "decorations">("tiles");
  const [cloudSignedIn, setCloudSignedIn] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState<UserCampaignRecord | null>(null);
  // `cloudBusy` is retained to gate the onboarding finalize flow; the manual save
  // affordance was removed in favor of the toolbar's subtle save status indicator.
  const [, setCloudBusy] = useState(false);
  const [cloudSaving, setCloudSaving] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingSubmitting, setOnboardingSubmitting] = useState(false);
  const [pendingOnboarding, setPendingOnboarding] = useState<PendingCloudOnboarding | null>(null);
  const [accountSetupState, setAccountSetupState] = useState<"idle" | "running" | "failed">("idle");
  const [accountSetupError, setAccountSetupError] = useState<string | null>(null);
  const skipNextCloudAutosaveRef = useRef(false);
  const finalizingPendingOnboardingRef = useRef(false);
  const tilePickerShortcutIdRef = useRef(0);
  const [tilePickerShortcutRequest, setTilePickerShortcutRequest] =
    useState<TilePickerShortcutRequest | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [anonymousNotebookOpen, setAnonymousNotebookOpen] = useState(false);
  const [anonymousNotebookContent, setAnonymousNotebookContent] = useState<RichTextDocument>(
    DEFAULT_ANONYMOUS_NOTEBOOK_CONTENT,
  );
  const [startFreshDialogOpen, setStartFreshDialogOpen] = useState(false);
  const [startFreshPending, setStartFreshPending] = useState(false);
  const activeCampaignId = activeCampaign?.id ?? null;
  const openKeyboardShortcuts = useKeyboardShortcutsStore((s) => s.open);
  const notebookOpenCampaignId = useCampaignNotebookStore((s) => s.openCampaignId);
  const openNotebookStore = useCampaignNotebookStore((s) => s.open);
  const closeNotebookStore = useCampaignNotebookStore((s) => s.close);
  const anonymousNotebookAvailable = !cloudSignedIn && entry.kind === "local-root";
  const campaignNotebookOpen = Boolean(activeCampaignId && notebookOpenCampaignId === activeCampaignId);
  const notebookOpen = anonymousNotebookAvailable ? anonymousNotebookOpen : campaignNotebookOpen;
  const [notebookHintActive, setNotebookHintActive] = useState(false);

  useEffect(() => {
    if (!cloudSignedIn || !activeCampaignId) {
      return;
    }
    if (hasSeenNotebookHint()) {
      return;
    }
    setNotebookHintActive(true);
  }, [cloudSignedIn, activeCampaignId]);

  useEffect(() => {
    if (anonymousNotebookAvailable) {
      setAnonymousNotebookContent(ensureAnonymousNotebookContent());
      let cancelled = false;

      void fetchStarterCampaignNotebookContent().then((content) => {
        if (cancelled || !content) {
          return;
        }
        setAnonymousNotebookContent(content);
        saveAnonymousNotebookContent(content);
      });

      return () => {
        cancelled = true;
      };
    }

    setAnonymousNotebookOpen(false);
  }, [anonymousNotebookAvailable]);

  const handleToggleNotebook = useCallback(() => {
    if (anonymousNotebookAvailable) {
      setAnonymousNotebookOpen((open) => {
        const nextOpen = !open;
        Sentry.logger.info(nextOpen ? "anonymous_notebook.open" : "anonymous_notebook.close");
        return nextOpen;
      });
      return;
    }

    if (!activeCampaignId) return;
    if (notebookHintActive) {
      setNotebookHintActive(false);
      markNotebookHintSeen();
    }
    if (notebookOpenCampaignId === activeCampaignId) {
      closeNotebookStore();
      Sentry.logger.info("campaign_notes.toolbar_close", { campaignId: activeCampaignId });
      return;
    }
    openNotebookStore(activeCampaignId);
    Sentry.logger.info("campaign_notes.toolbar_open", { campaignId: activeCampaignId });
  }, [
    anonymousNotebookAvailable,
    activeCampaignId,
    closeNotebookStore,
    notebookHintActive,
    notebookOpenCampaignId,
    openNotebookStore,
  ]);
  const remoteCampaignQuery = useCampaignByShortIdQuery(
    entry.kind === "cloud-campaign" ? entry.shortId : "",
    undefined,
    assetsReady && cloudSignedIn && entry.kind === "cloud-campaign",
  );
  const updateCampaignMutation = useUpdateCampaignMutation();
  const updateCampaignMutationRef = useRef(updateCampaignMutation);
  updateCampaignMutationRef.current = updateCampaignMutation;

  const effective = useEffectiveCampaignRole(activeCampaign);
  const isPreviewingAsPlayer = effective.isPreviewing;
  const effectiveCanEdit = effective.effectiveCanEdit;
  const actualCanEditCampaign = Boolean(activeCampaign?.capabilities.canEditCampaign);
  // Signed-out visitors are always editing the local-only map (persisted to
  // IndexedDB on this device). They have no campaign role, so we grant edit
  // access unconditionally; cloud-only affordances (notes, templates, invites,
  // etc.) remain gated on `effectiveCanEdit` + `activeCampaignId`.
  const localOnlyEditing = !cloudSignedIn;
  const canEditEffective = localOnlyEditing || effectiveCanEdit;

  useEffect(() => {
    setCanEdit(canEditEffective);
  }, [canEditEffective, setCanEdit]);

  useEffect(() => {
    if (!cloudSignedIn && activeTool === "map-note") {
      setActiveTool("pointer");
    }
  }, [activeTool, cloudSignedIn, setActiveTool]);

  useEffect(() => {
    if (entry.kind !== "cloud-campaign") {
      useMapNoteDetailStore.getState().close();
      return;
    }
    if (!activeCampaignId) return;
    if (routedMapNoteId) {
      useMapNoteDetailStore.getState().open(routedMapNoteId);
      return;
    }
    useMapNoteDetailStore.getState().close();
  }, [activeCampaignId, entry.kind, routedMapNoteId]);

  useEffect(() => {
    Sentry.setTag("view_as", isPreviewingAsPlayer ? "player" : "actual");
  }, [isPreviewingAsPlayer]);

  const handleRemoteMapChange = useCallback(
    (snapshot: MapChangeSnapshot) => {
      useMapStore.getState().applyMapChanges(snapshot);
    },
    [],
  );

  const handleRemotePartyMove = useCallback(
    (target: Hex) => {
      useMapStore.getState().setParty(target);
    },
    [],
  );

  const queryClient = useQueryClient();

  const handleRemoteMapNoteChange = useCallback(
    () => {
      if (!activeCampaignId) return;
      void queryClient.invalidateQueries({
        queryKey: queryKeys.mapNotes.list(activeCampaignId).slice(0, 3),
      });
    },
    [queryClient, activeCampaignId],
  );

  const handleRemoteDocumentUpdate = useCallback(
    (document: import("@west-marches/shared").MapDocument) => {
      try {
        const imported = importMapDocument(document);
        const localMap = useMapStore.getState().mapState;
        if (!localMap) {
          return;
        }
        const remoteSavedAt = new Date(document.savedAt).getTime();
        const localDoc = createMapDocument(localMap, { history, historyIndex });
        const localSavedAt = new Date(localDoc.savedAt).getTime();
        if (remoteSavedAt > localSavedAt) {
          importMap(imported.mapState);
        }
      } catch {
        // Ignore malformed documents from postgres_changes
      }
    },
    [history, historyIndex, importMap],
  );

  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [authDisplayName, setAuthDisplayName] = useState<string | null>(null);

  useEffect(() => {
    if (!cloudSignedIn) {
      setAuthUserId(null);
      setAuthDisplayName(null);
      return;
    }
    void getSupabaseCurrentUser()
      .then((user) => {
        setAuthUserId(user?.id ?? null);
        setAuthDisplayName(user?.user_metadata?.display_name ?? user?.email ?? null);
      })
      .catch((error) => {
        Sentry.logger.warn("HomeClient getUser failed", {
          error: error instanceof Error ? error.message : String(error),
        });
        setAuthUserId(null);
        setAuthDisplayName(null);
      });
  }, [cloudSignedIn]);

  const profileQuery = useCurrentProfileQuery(undefined, { enabled: cloudSignedIn });
  const accountProfile = profileQuery.data ?? null;

  const openCampaignsModal = useCampaignsModalStore((s) => s.openModal);
  const handleOpenCampaigns = useCallback(() => {
    openCampaignsModal(activeCampaign?.id ?? null);
  }, [openCampaignsModal, activeCampaign?.id]);

  useEffect(() => {
    if (!authUserId || !activeCampaign?.shortId) {
      return;
    }
    setLastOpenedShortId(authUserId, activeCampaign.shortId);
  }, [authUserId, activeCampaign?.shortId]);

  const { onlineUsers, broadcastMapChange, broadcastPartyMove, broadcastMapNoteChange } = useCampaignRealtimeChannel({
    campaignId: activeCampaignId,
    userId: authUserId,
    displayName: authDisplayName ?? activeCampaign?.roster?.find((m) => m.userId === authUserId)?.displayName ?? null,
    myRole: effective.effectiveRole ?? activeCampaign?.myRole ?? null,
    canEdit,
    onRemoteMapChange: handleRemoteMapChange,
    onRemotePartyMove: handleRemotePartyMove,
    onRemoteDocumentUpdate: handleRemoteDocumentUpdate,
    onRemoteMapNoteChange: handleRemoteMapNoteChange,
  });

  const broadcastMapChangeRef = useRef(broadcastMapChange);
  broadcastMapChangeRef.current = broadcastMapChange;
  const broadcastPartyMoveRef = useRef(broadcastPartyMove);
  broadcastPartyMoveRef.current = broadcastPartyMove;

  useEffect(() => {
    setBroadcastMapChange(broadcastMapChange);
    setBroadcastPartyMove(broadcastPartyMove);
    setBroadcastMapNoteChange(broadcastMapNoteChange);
    return () => {
      setBroadcastMapChange(() => {});
      setBroadcastPartyMove(() => {});
      setBroadcastMapNoteChange(() => {});
    };
  }, [broadcastMapChange, broadcastPartyMove, broadcastMapNoteChange]);

  useEffect(() => {
    async function load() {
      await ensureTileCatalogLoaded();
      await preloadMarker();
      setAssetsReady(true);
    }
    void load();
  }, [ensureTileCatalogLoaded]);

  const applyImportedDocument = useCallback(
    (document: ImportedMapDocument) => {
      importMap(document.mapState);
      restoreEditorPersistence(document.editorState);
    },
    [importMap, restoreEditorPersistence],
  );

  const preloadAndShow = useCallback(async (): Promise<void> => {
    const ms = useMapStore.getState().mapState;
    if (!ms) {
      setSessionReady(true);
      return;
    }

    const sortedKeys = getMapHexKeysSortedByY(ms.hexes);
    const width = typeof window !== "undefined" ? window.innerWidth : 1280;
    const height = typeof window !== "undefined" ? window.innerHeight : 800;
    const visibleKeys = getVisibleHexKeys(
      ms.hexes,
      sortedKeys,
      DEFAULT_MAP_CAMERA,
      width,
      height,
    );

    const visibleAssets = collectMapPreloadAssets(
      ms.hexes,
      ms.decorations,
      visibleKeys,
    );

    setPreloadProgress({ loaded: 0, total: 0 });
    try {
      await preloadMapAssets(visibleAssets, (loaded, total) => {
        setPreloadProgress({ loaded, total });
      });
    } catch (error) {
      // preloadMapAssets uses Promise.allSettled internally so this catch is
      // defensive only; we still want to reveal the map if something explodes.
      Sentry.captureException(error, {
        tags: { area: "home-client", action: "preload-visible-assets" },
      });
    }

    setSessionReady(true);
    setPreloadProgress(null);

    const visibleKeySet = new Set(visibleKeys);
    const remainingKeys = sortedKeys.filter((key) => !visibleKeySet.has(key));
    if (remainingKeys.length > 0) {
      const remainingAssets = collectMapPreloadAssets(
        ms.hexes,
        ms.decorations,
        remainingKeys,
      );
      void preloadMapAssets(remainingAssets).catch((error) => {
        Sentry.captureException(error, {
          tags: { area: "home-client", action: "preload-remaining-assets" },
        });
      });
    }
  }, []);

  const restoreLocalSessionOrCreate = useCallback(async (): Promise<void> => {
    const savedSession = await loadMapSession().catch(() => null);
    if (savedSession) {
      applyImportedDocument(savedSession);
      return;
    }

    const legacySession = loadLegacyMapSession();
    if (legacySession) {
      applyImportedDocument(legacySession);
      clearLegacyMapSession();
      return;
    }

    // First-time visitors with no local autosave see the curated starter
    // campaign (if one is configured in the DB). Falls back to a blank ocean
    // rectangle when no starter exists or the fetch fails.
    const starter = await fetchStarterCampaignDocument().catch(() => null);
    if (starter) {
      const imported = importMapDocument(starter);
      applyImportedDocument(imported);
      return;
    }

    initMap(DEFAULT_NEW_MAP_BOUNDS);
    restoreEditorPersistence(EMPTY_EDITOR_PERSISTENCE_STATE);
  }, [applyImportedDocument, initMap, restoreEditorPersistence]);

  const resetLocalMapToBlank = useCallback(async () => {
    await clearMapSession().catch((error) => {
      reportHomeClientError("clear-map-session", error);
    });
    clearLegacyMapSession();
    setAnonymousNotebookOpen(false);
    useMapNoteDetailStore.getState().close();
    initMap(DEFAULT_NEW_MAP_BOUNDS);
    restoreEditorPersistence(EMPTY_EDITOR_PERSISTENCE_STATE);
    setActiveTool("pointer");
  }, [initMap, restoreEditorPersistence, setActiveTool]);

  useEffect(() => {
    if (!assetsReady) {
      return;
    }
    let cancelled = false;

    async function restoreSession() {
      const user = await getSupabaseCurrentUser();

      if (cancelled) {
        return;
      }

      const signedIn = Boolean(user);
      setCloudSignedIn(signedIn);

      if (signedIn && entry.kind === "cloud-campaign") {
        return;
      }

      if (signedIn && entry.kind === "local-root") {
        const pending = loadPendingCloudOnboarding();
        if (pending && !cancelled) {
          // Use the functional setter so we don't overwrite an existing
          // pendingOnboarding with a fresh-from-localStorage object whose
          // identity differs. Doing so would change the dep of the finalize
          // effect and cancel the in-flight finalize mid-request, leaving
          // the "Setting up your account" modal stuck. We only want to
          // populate state when recovering after a page reload.
          setPendingOnboarding((prev) => prev ?? pending);
          setAccountSetupError(null);
          setAccountSetupState((prev) => (prev === "idle" ? "running" : prev));
        }
      }

      await restoreLocalSessionOrCreate();
      if (!cancelled) {
        await preloadAndShow();
      }
    }

    void restoreSession().catch((error) => {
      reportHomeClientError("restore-session", error);
      if (!cancelled) {
        void restoreLocalSessionOrCreate().finally(() => {
          if (!cancelled) {
            void preloadAndShow();
          }
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [applyImportedDocument, assetsReady, entry, preloadAndShow, restoreLocalSessionOrCreate, supabase, toast]);

  useEffect(() => {
    if (!assetsReady || sessionReady || entry.kind !== "cloud-campaign" || !cloudSignedIn) {
      return;
    }

    if (remoteCampaignQuery.data) {
      skipNextCloudAutosaveRef.current = true;
      applyImportedDocument(importMapDocument(remoteCampaignQuery.data.document));
      setActiveCampaign(remoteCampaignQuery.data);
      setActiveCampaignId(remoteCampaignQuery.data.id);
      void preloadAndShow();
      return;
    }

    if (!remoteCampaignQuery.isError) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const cloudEntry = entry.kind === "cloud-campaign" ? entry : null;
      const failedShortId = cloudEntry?.shortId ?? null;

      // Differentiate "user no longer has access" from "campaign doesn't exist /
      // network error" by probing the unauthenticated public join-info endpoint.
      // The authenticated fetch hides existence via RLS, so we cannot tell
      // which case we are in without this extra probe.
      let publicInfo: Awaited<ReturnType<typeof getPublicCampaignJoinInfo>> | null = null;
      if (failedShortId) {
        try {
          publicInfo = await getPublicCampaignJoinInfo(failedShortId);
        } catch {
          publicInfo = null;
        }
      }

      if (cancelled) {
        return;
      }

      if (publicInfo && failedShortId) {
        Sentry.logger.info("home_client.removed_from_campaign_detected", {
          shortId: failedShortId,
        });

        try {
          if (authUserId && getLastOpenedShortId(authUserId) === failedShortId) {
            clearLastOpenedShortId(authUserId);
          }

          const userCampaigns = await queryClient.fetchQuery({
            queryKey: queryKeys.campaigns.list(),
            queryFn: listUserCampaigns,
          });
          if (cancelled) {
            return;
          }

          const remaining = userCampaigns.filter(
            (campaign) => campaign.shortId !== failedShortId,
          );
          const nextShortId = resolveDefaultCampaignShortId(remaining, authUserId);

          setRemovedCampaignNotice({
            shortId: failedShortId,
            name: publicInfo.campaignName,
          });

          if (nextShortId) {
            navigate({
              to: "/campaign/$shortId",
              params: { shortId: nextShortId },
              replace: true,
            });
          } else {
            navigate({ to: "/", replace: true });
          }
        } catch (error) {
          reportHomeClientError("removed-from-campaign-redirect", error, {
            shortId: failedShortId,
          });
          await restoreLocalSessionOrCreate();
          if (!cancelled) {
            await preloadAndShow();
          }
        }
        return;
      }

      toast.error(
        remoteCampaignQuery.error instanceof Error
          ? `${remoteCampaignQuery.error.message}. Using local autosave.`
          : "Cloud campaign unavailable. Using local autosave.",
      );
      await restoreLocalSessionOrCreate();
      if (!cancelled) {
        await preloadAndShow();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    applyImportedDocument,
    assetsReady,
    authUserId,
    cloudSignedIn,
    entry,
    navigate,
    preloadAndShow,
    queryClient,
    remoteCampaignQuery.data,
    remoteCampaignQuery.error,
    remoteCampaignQuery.isError,
    restoreLocalSessionOrCreate,
    sessionReady,
    toast,
  ]);

  useEffect(() => {
    if (!assetsReady || !sessionReady || !mapState) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void saveMapSession(createMapDocument(mapState, { history, historyIndex }));
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [assetsReady, sessionReady, mapState, history, historyIndex]);

  const saveActiveCampaign = useCallback(
    async (manual: boolean) => {
      if (!activeCampaignId || !mapState) {
        return;
      }

      if (isPreviewingAsPlayer) {
        if (manual) {
          toast.info("Saving is disabled while viewing as a player.");
        }
        return;
      }

      if (manual) {
        setCloudBusy(true);
      }
      setCloudSaving(true);

      try {
        const document = createMapDocument(mapState, { history, historyIndex });
        const campaign = await updateCampaignMutationRef.current.mutateAsync({
          campaignId: activeCampaignId,
          input: {
            document,
          },
        });
        setActiveCampaign((prev) => prev ? { ...prev, ...campaign } : prev);
        if (manual) {
          toast.success(`Saved "${campaign.name}" to cloud.`);
        }
      } catch (error) {
        if (manual) {
          reportHomeClientError("save-cloud-campaign", error, {
            activeCampaignId,
            entryKind: entry.kind,
          });
        }
        if (manual) {
          toast.error(error instanceof Error ? error.message : "Cloud save failed.");
        }
      } finally {
        if (manual) {
          setCloudBusy(false);
        }
        setCloudSaving(false);
      }
    },
    [activeCampaignId, history, historyIndex, mapState, toast],
  );

  useEffect(() => {
    if (!assetsReady || !sessionReady || !cloudSignedIn || !activeCampaignId || !mapState) {
      return;
    }

    if (skipNextCloudAutosaveRef.current) {
      skipNextCloudAutosaveRef.current = false;
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void saveActiveCampaign(false);
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    activeCampaignId,
    assetsReady,
    cloudSignedIn,
    history,
    historyIndex,
    mapState,
    saveActiveCampaign,
    sessionReady,
  ]);

  useEffect(() => {
    if (
      !assetsReady ||
      !sessionReady ||
      !cloudSignedIn ||
      !pendingOnboarding ||
      accountSetupState !== "running" ||
      finalizingPendingOnboardingRef.current
    ) {
      return;
    }

    // Use the document captured at signup-submit time. This intentionally
    // ignores the live editor `mapState` so finalize works even when the URL
    // points at a stale or non-existent cloud campaign — the user's intent is
    // to save the local map they drew before signing up.
    const currentPendingOnboarding = pendingOnboarding;
    let cancelled = false;
    finalizingPendingOnboardingRef.current = true;
    setCloudBusy(true);
    setAccountSetupError(null);

    async function finalizePending() {
      try {
        const { campaign } = await finalizePendingCloudOnboarding({
          pending: currentPendingOnboarding,
          document: currentPendingOnboarding.document,
          updateDisplayName: async (displayName) => {
            await updateCurrentProfile({ displayName });
          },
        });

        if (cancelled) {
          return;
        }

        clearPendingCloudOnboarding();
        clearAnonymousNotebookContent();
        setPendingOnboarding(null);
        // Dismiss the "Setting up your account" modal explicitly. We can't
        // rely on the route change alone because navigating between two
        // `/campaign/<shortId>` URLs (e.g. signing up while sitting on a
        // stale campaign link) keeps the same `HomeClient` instance mounted,
        // which would otherwise leave `accountSetupState === "running"` and
        // the modal visible forever.
        finalizingPendingOnboardingRef.current = false;
        setAccountSetupError(null);
        setAccountSetupState("idle");
        // Force the cloud-campaign bootstrap effect to re-fire for the
        // freshly created campaign. Otherwise — when `HomeClient` stays
        // mounted across `/campaign/<staleId>` → `/campaign/<newShortId>`
        // — `activeCampaign`/autosave/realtime never initialize because
        // the bootstrap effect early-returns on `sessionReady`.
        setActiveCampaign(null);
        setActiveCampaignId(null);
        setSessionReady(false);
        navigate({ to: "/campaign/$shortId", params: { shortId: campaign.shortId }, replace: true });
      } catch (error) {
        reportHomeClientError("finalize-pending-onboarding", error, {
          pendingCampaignName: currentPendingOnboarding.campaignName,
        });
        if (!cancelled) {
          finalizingPendingOnboardingRef.current = false;
          setAccountSetupError(
            error instanceof Error ? error.message : "Failed to finish creating your campaign.",
          );
          setAccountSetupState("failed");
        }
      } finally {
        if (!cancelled) {
          setCloudBusy(false);
        }
      }
    }

    void finalizePending();

    return () => {
      cancelled = true;
    };
  }, [
    assetsReady,
    cloudSignedIn,
    navigate,
    pendingOnboarding,
    sessionReady,
    accountSetupState,
  ]);

  const handleUndo = useCallback(() => {
    if (!canUndo() || !mapState) return;
    const entry = history[historyIndex];
    applySnapshot(entry.before);
    editorUndo();
    broadcastMapChangeRef.current(entry.before);
  }, [canUndo, mapState, history, historyIndex, applySnapshot, editorUndo]);

  const handleRedo = useCallback(() => {
    if (!canRedo() || !mapState) return;
    const entry = history[historyIndex + 1];
    applySnapshot(entry.after);
    editorRedo();
    broadcastMapChangeRef.current(entry.after);
  }, [canRedo, mapState, history, historyIndex, applySnapshot, editorRedo]);

  const dispatchTilePickerShortcut = useCallback(
    (request: TilePickerShortcutRequestInput) => {
      tilePickerShortcutIdRef.current += 1;
      setTilePickerShortcutRequest({
        id: tilePickerShortcutIdRef.current,
        ...request,
      });
    },
    [],
  );

  useEffect(() => {
    if (!tilePickerOpen) {
      setTilePickerShortcutRequest(null);
    }
  }, [tilePickerOpen]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const key = e.key.toLowerCase();
      const activeTileSet =
        activeTool === "tile-brush" && selectedTileSetId ? getTileSetById(selectedTileSetId) : null;
      const orderedBrushEntries =
        activeTileSet && isOrderedTileSet(activeTileSet) ? getOrderedBrushEntries(activeTileSet) : [];
      const activeDecorationPaintSet =
        activeTool === "decoration-brush" && selectedDecorationPaintSetId
          ? getDecorationPaintSetById(selectedDecorationPaintSetId)
          : null;
      const orderedDecorationBrushEntries =
        activeDecorationPaintSet && isOrderedDecorationPaintSet(activeDecorationPaintSet)
        ? getOrderedDecorationBrushEntries(activeDecorationPaintSet)
        : [];
      const action = resolveEditorShortcut({
        key,
        code: e.code,
        view: "worldMap",
        hasModifier: e.metaKey || e.ctrlKey,
        altKey: e.altKey,
        shiftKey: e.shiftKey,
        repeat: e.repeat,
        isEditableTarget: isEditableTarget(e.target),
        tilePickerOpen,
        decorationSelectionActive: Boolean(selectedDecorationPaintSetId),
        activeTool,
        editorMode,
        orderedBrushEntryCount: orderedBrushEntries.length,
        decorationEntryCount: orderedDecorationBrushEntries.length,
        mapNotesEnabled: cloudSignedIn,
        campaignNotebookAvailable: Boolean(activeCampaignId || anonymousNotebookAvailable),
      });
      if (!action) {
        return;
      }

      e.preventDefault();
      if (e.target instanceof HTMLElement && !isEditableTarget(e.target)) {
        e.target.blur();
      }

      if (action.kind === "toggle-tile-picker") {
        setTilePickerOpen((v) => !v);
      } else if (action.kind === "undo") {
        handleUndo();
      } else if (action.kind === "redo") {
        handleRedo();
      } else if (action.kind === "set-editor-mode") {
        setEditorMode(action.mode);
      } else if (action.kind === "tile-picker-shortcut") {
        dispatchTilePickerShortcut(action.request);
      } else if (action.kind === "close-brush") {
        setActiveTool("pointer");
      } else if (action.kind === "select-ordered-brush-index") {
        setSelectedBrushEntry(orderedBrushEntries[action.index] ?? null);
      } else if (action.kind === "cycle-ordered-brush") {
        cycleSelectedBrushEntry(action.step);
      } else if (action.kind === "cycle-decoration-selection") {
        cycleSelectedDecorationBrushEntry(action.step);
      } else if (action.kind === "set-decoration-mode") {
        setDecorationMode(action.mode);
      } else if (action.kind === "set-tool") {
        setActiveTool(action.tool);
      } else if (action.kind === "open-brush-picker") {
        setActiveTool(action.mode === "decorations" ? "decoration-brush" : "tile-brush");
        setTilePickerMode(action.mode);
        setTilePickerOpen(true);
      } else if (action.kind === "toggle-campaign-notebook") {
        if (anonymousNotebookAvailable) {
          setAnonymousNotebookOpen((open) => {
            const nextOpen = !open;
            Sentry.logger.info(nextOpen ? "anonymous_notebook.shortcut_open" : "anonymous_notebook.shortcut_close");
            return nextOpen;
          });
        } else {
          if (!activeCampaignId) return;
          const notebookState = useCampaignNotebookStore.getState();
          if (notebookState.openCampaignId === activeCampaignId) {
            notebookState.close();
            Sentry.logger.info("campaign_notes.shortcut_close", { campaignId: activeCampaignId });
          } else {
            notebookState.open(activeCampaignId);
            Sentry.logger.info("campaign_notes.shortcut_open", { campaignId: activeCampaignId });
          }
        }
      } else if (action.kind === "toggle-keyboard-shortcuts") {
        const shortcutsState = useKeyboardShortcutsStore.getState();
        shortcutsState.toggle();
        Sentry.logger.info("keyboard_shortcuts.shortcut_toggle");
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeTool,
    editorMode,
    cycleSelectedBrushEntry,
    cycleSelectedDecorationBrushEntry,
    dispatchTilePickerShortcut,
    handleUndo,
    handleRedo,
    setEditorMode,
    selectedTileSetId,
    selectedDecorationPaintSetId,
    setDecorationMode,
    setSelectedBrushEntry,
    setActiveTool,
    tilePickerOpen,
    effectiveCanEdit,
    activeCampaignId,
    anonymousNotebookAvailable,
  ]);

  const handleOpenSignIn = useCallback(() => {
    setOnboardingOpen(true);
  }, []);

  const handleConfirmStartFresh = useCallback(async () => {
    setStartFreshPending(true);
    try {
      await resetLocalMapToBlank();
      setStartFreshDialogOpen(false);
    } finally {
      setStartFreshPending(false);
    }
  }, [resetLocalMapToBlank]);

  const canManageJoinCode =
    effective.effectiveCapabilities.canManageJoinCode && !isPreviewingAsPlayer;

  const handleOpenInvite = useCallback(() => {
    setInviteModalOpen(true);
  }, []);

  const handleOpenMembers = useCallback(() => {
    setMembersModalOpen(true);
  }, []);

  const handleCloseInvite = useCallback(() => {
    setInviteModalOpen(false);
  }, []);

  const handleCloseMembers = useCallback(() => {
    setMembersModalOpen(false);
  }, []);

  const handleInviteCampaignUpdate = useCallback(
    (updates: Partial<UserCampaignRecord>) => {
      setActiveCampaign((prev) => (prev ? { ...prev, ...updates } : prev));
    },
    [],
  );

  const handleMembersRosterUpdate = useCallback(
    (roster: UserCampaignRecord["roster"]) => {
      setActiveCampaign((prev) => (prev ? { ...prev, roster } : prev));
    },
    [],
  );

  const handleSignIn = useCallback(
    async (email: string, password: string) => {
      setOnboardingSubmitting(true);
      try {
        await signInWithPassword({ supabase, email, password });
        setCloudSignedIn(true);
        setOnboardingOpen(false);
        navigate({ to: "/", replace: true });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to sign in.");
      } finally {
        setOnboardingSubmitting(false);
      }
    },
    [navigate, supabase, toast],
  );

  const handleSubmitOnboarding = useCallback(
    async (values: OnboardingValues) => {
      if (!mapState) {
        throw new Error("The map is still loading. Give it a moment, then try again.");
      }

      const payload: PendingCloudOnboarding = {
        campaignName: values.campaignName.trim(),
        displayName: values.displayName.trim(),
        email: values.email.trim(),
        document: createMapDocument(mapState, { history, historyIndex }),
        anonymousNotebookContent: anonymousNotebookAvailable ? anonymousNotebookContent : undefined,
        createdAt: new Date().toISOString(),
      };

      setOnboardingSubmitting(true);
      savePendingCloudOnboarding(payload);
      setPendingOnboarding(payload);

      try {
        await signUpWithPassword({
          supabase,
          email: payload.email,
          password: values.password,
        });
        setCloudSignedIn(true);
        setOnboardingOpen(false);
        setAccountSetupState("running");
        // The finalize effect (no longer gated by `entry.kind`) will fire
        // here in *this* `HomeClient` instance regardless of the URL, then
        // navigate directly to the newly-created campaign on success.
        // We deliberately do NOT pre-redirect to `/` first: a `/` →
        // `/campaign/<x>` transition unmounts/remounts `HomeClient` and we
        // briefly run TWO instances, which races against the finalize
        // effect and can produce duplicate campaigns / a stuck modal.
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to create your account.");
        throw error;
      } finally {
        setOnboardingSubmitting(false);
      }
    },
    [anonymousNotebookAvailable, anonymousNotebookContent, history, historyIndex, mapState, supabase, toast],
  );

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <Toolbar
        onUndo={handleUndo}
        onRedo={handleRedo}
        onOpenSignIn={handleOpenSignIn}
        onStartFresh={
          anonymousNotebookAvailable && sessionReady ? () => setStartFreshDialogOpen(true) : undefined
        }
        onOpenInvite={handleOpenInvite}
        onOpenCampaigns={handleOpenCampaigns}
        onOpenMembers={handleOpenMembers}
        onOpenKeyboardShortcuts={openKeyboardShortcuts}
        canUndo={canUndo()}
        canRedo={canRedo()}
        cloudSignedIn={cloudSignedIn}
        cloudSaving={cloudSaving}
        hasActiveCampaign={Boolean(activeCampaign)}
        campaignShortId={activeCampaign?.shortId ?? null}
        onlineUsers={onlineUsers}
        currentUserId={authUserId}
        canToggleViewAs={effective.canToggleViewAs}
        isPreviewingAsPlayer={isPreviewingAsPlayer}
        canInvite={canManageJoinCode}
        accountProfile={accountProfile}
        onToggleNotebook={
          activeCampaignId || anonymousNotebookAvailable ? handleToggleNotebook : undefined
        }
        notebookOpen={notebookOpen}
        notebookHighlightFirstTime={notebookHintActive && !notebookOpen}
      />
      {cloudSignedIn ? (
        <CampaignsModal
          activeCampaignShortId={activeCampaign?.shortId ?? null}
          userId={authUserId}
        />
      ) : null}
      <InviteModal
        open={inviteModalOpen}
        onClose={handleCloseInvite}
        onOpenMembers={handleOpenMembers}
        campaign={activeCampaign}
        onCampaignUpdate={handleInviteCampaignUpdate}
      />
      <MembersModal
        open={membersModalOpen}
        onClose={handleCloseMembers}
        campaign={activeCampaign}
        canManageMembers={Boolean(activeCampaign?.capabilities.canManageMembers) && !isPreviewingAsPlayer}
        onRosterUpdate={handleMembersRosterUpdate}
      />
      <OnboardingModal
        open={onboardingOpen || accountSetupState !== "idle"}
        submitting={onboardingSubmitting}
        phase={accountSetupState === "idle" ? "collect" : "setup"}
        initialValues={pendingOnboarding ?? undefined}
        setupError={accountSetupState === "failed" ? accountSetupError : null}
        onClose={() => setOnboardingOpen(false)}
        onSignIn={handleSignIn}
        onSubmit={handleSubmitOnboarding}
        onRetrySetup={() => {
          finalizingPendingOnboardingRef.current = false;
          setAccountSetupError(null);
          setAccountSetupState("running");
        }}
        onDismissSetup={() => {
          finalizingPendingOnboardingRef.current = false;
          setAccountSetupError(null);
          setAccountSetupState("idle");
        }}
      />
      <ConfirmDialog
        open={startFreshDialogOpen}
        title="Start fresh?"
        description="This clears the local autosave on this device and opens a blank water map. Your welcome notebook will stay available."
        confirmLabel="Start fresh"
        cancelLabel="Keep example"
        destructive
        isPending={startFreshPending}
        onConfirm={handleConfirmStartFresh}
        onCancel={() => setStartFreshDialogOpen(false)}
      />
      {canEdit && tilePickerOpen && (
        <TilePicker
          mode={tilePickerMode}
          onClose={() => setTilePickerOpen(false)}
          shortcutRequest={tilePickerShortcutRequest}
        />
      )}
      {(!assetsReady || !sessionReady) && (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="flex w-72 flex-col items-center gap-3">
            <p className="text-lg text-gray-400">
              {!assetsReady
                ? "Loading tile assets..."
                : preloadProgress && preloadProgress.total > 0
                  ? `Preparing map... (${preloadProgress.loaded} / ${preloadProgress.total})`
                  : "Restoring your map..."}
            </p>
            {assetsReady && preloadProgress && preloadProgress.total > 0 && (
              <div className="h-1 w-full overflow-hidden rounded bg-gray-800">
                <div
                  className="h-full bg-gray-200 transition-[width] duration-150 ease-out"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round(
                        (preloadProgress.loaded / preloadProgress.total) * 100,
                      ),
                    )}%`,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {canEdit && <OrderedBrushStrip />}
      {canEdit && <SelectedDecorationStrip />}
      {canEdit ? (
        <BottomToolbar
          onTileBrushClick={() => {
            setActiveTool("tile-brush");
            setTilePickerMode("tiles");
            setTilePickerOpen(true);
          }}
          onDecorationBrushClick={() => {
            setActiveTool("decoration-brush");
            setTilePickerMode("decorations");
            setTilePickerOpen(true);
          }}
          cloudSignedIn={cloudSignedIn}
        />
      ) : null}
      {mapState && assetsReady && sessionReady && <MapCanvas />}
      <Suspense fallback={null}>
        <KeyboardShortcutsModalLazy />
      </Suspense>
      {entry.kind === "cloud-campaign" && (
        <Suspense fallback={null}>
          <MapNoteDetailModalLazy
            campaignId={activeCampaignId}
            shortId={activeCampaign?.shortId ?? entry.shortId}
            canEdit={effectiveCanEdit}
            isPreviewingAsPlayer={isPreviewingAsPlayer}
          />
        </Suspense>
      )}
      {anonymousNotebookAvailable && (
        <Suspense fallback={null}>
          <AnonymousNotebookModalLazy
            open={anonymousNotebookOpen}
            content={anonymousNotebookContent}
            onClose={() => setAnonymousNotebookOpen(false)}
          />
        </Suspense>
      )}
      {cloudSignedIn && activeCampaignId && (
        <Suspense fallback={null}>
          <CampaignNotebookModalLazy
            campaignId={activeCampaignId}
            canEdit={actualCanEditCampaign}
            isPreviewingAsPlayer={isPreviewingAsPlayer}
          />
        </Suspense>
      )}
      {effectiveCanEdit && (
        <Suspense fallback={null}>
          <MapNoteTemplateManagerModalLazy />
        </Suspense>
      )}
      <div className="pointer-events-none absolute bottom-4 right-4 z-10 flex flex-col items-end gap-2">
        <BetaBadge className="pointer-events-auto" />
        <ArtAttributionBadge
          variant="canvas-corner"
          className="pointer-events-auto"
        />
      </div>
    </div>
  );
}

const MapNoteDetailModalLazy = lazy(() => import("@/components/MapNoteDetailModal"));
const AnonymousNotebookModalLazy = lazy(() => import("@/components/AnonymousNotebookModal"));
const CampaignNotebookModalLazy = lazy(() => import("@/components/CampaignNotebookModal"));
const KeyboardShortcutsModalLazy = lazy(() => import("@/components/KeyboardShortcutsModal"));
const MapNoteTemplateManagerModalLazy = lazy(() => import("@/components/MapNoteTemplateManagerModal"));

